import { EnrollmentLandingPageInfo, EnrollmentType, MasterClientProductViewDto } from '../models/api-data-contracts';
import { filterProductsActiveOn } from './helpers';

/** One actionable enrollment path on the public landing (OE / NH). */
export interface LandingPathOption {
  type: EnrollmentType;
  /** Short action line on the button (no dates — see `caption`) */
  label: string;
  /** Secondary line under the button, e.g. effective date (not part of the hit target) */
  caption?: string;
  /** Primary CTA vs alternate when both paths exist; single path is always primary */
  primary: boolean;
  /** Full intent for assistive tech (action + dates) */
  ariaLabel: string;
}

export type LandingEnrollmentWizardResolution =
  | { enrollmentType: EnrollmentType; redirectToPathPicker: false }
  | { enrollmentType: null; redirectToPathPicker: true }
  | { enrollmentType: null; redirectToPathPicker: false };

/**
 * Pure rules for which enrollment paths (OE / NH) apply to a landing page payload.
 * Used by the client landing UI and the enroll wizard.
 */
export class EnrollmentLandingPathRules {
  static isOpenEnrollmentPath(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    if (!info) return false;
    const status = (info.status ?? '').toString().toLowerCase();
    return status === 'active' && !!info.effectiveDate;
  }

  /** Footer cancel link: Active OE window only (not always-open, future, expired, or unavailable). */
  static canRequestOpenEnrollmentCancellation(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    if (!info) return false;
    if (info.alwaysOpenEnrollment !== false) return false;
    const status = (info.status ?? '').toString().toLowerCase();
    return status === 'active' && !!info.effectiveDate;
  }

  static isNewHirePath(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    return info?.allowNewHireEnrollment === true;
  }

  static isBothPaths(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    return (
      EnrollmentLandingPathRules.isOpenEnrollmentPath(info) &&
      EnrollmentLandingPathRules.isNewHirePath(info)
    );
  }

  static isOpenEnrollmentOnlyPath(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    return (
      EnrollmentLandingPathRules.isOpenEnrollmentPath(info) &&
      !EnrollmentLandingPathRules.isNewHirePath(info)
    );
  }

  static isNewHireOnlyPath(info: EnrollmentLandingPageInfo | null | undefined): boolean {
    return (
      EnrollmentLandingPathRules.isNewHirePath(info) &&
      !EnrollmentLandingPathRules.isOpenEnrollmentPath(info)
    );
  }

  static parseEnrollmentTypeQuery(value: string | null | undefined): EnrollmentType | null {
    const v = value?.trim().toUpperCase();
    if (v === 'OE' || v === 'NH') return v;
    return null;
  }

  static formatLandingDateForDisplay(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  }

  /** Buttons to show on the landing sticky bar (0, 1, or 2). */
  static buildPathOptions(info: EnrollmentLandingPageInfo | null | undefined): LandingPathOption[] {
    if (!info) return [];

    if (info.alwaysOpenEnrollment === true) {
      const nhDate = EnrollmentLandingPathRules.formatLandingDateForDisplay(info.newHireEffectiveDate ?? undefined);
      const captionForDate = (dateStr: string) => `(Coverage effective ${dateStr})`;
      return [
        {
          type: 'OE',
          label: 'Continue enrollment',
          caption: nhDate ? captionForDate(nhDate) : undefined,
          primary: true,
          ariaLabel: nhDate
            ? `Continue enrollment, coverage effective ${nhDate}`
            : 'Continue enrollment',
        },
      ];
    }

    const oe = EnrollmentLandingPathRules.isOpenEnrollmentPath(info);
    const nh = EnrollmentLandingPathRules.isNewHirePath(info);
    if (!oe && !nh) return [];

    const eff = EnrollmentLandingPathRules.formatLandingDateForDisplay(info.effectiveDate ?? undefined);
    const nhDate = EnrollmentLandingPathRules.formatLandingDateForDisplay(info.newHireEffectiveDate ?? undefined);

    const captionForDate = (dateStr: string) => `(Coverage effective ${dateStr})`;

    const options: LandingPathOption[] = [];

    if (oe) {
      options.push({
        type: 'OE',
        label: 'Continue with open enrollment',
        caption: eff ? captionForDate(eff) : undefined,
        primary: true,
        ariaLabel: eff
          ? `Continue with open enrollment, coverage effective ${eff}`
          : 'Continue with open enrollment',
      });
    }

    if (nh) {
      options.push({
        type: 'NH',
        label: 'Enroll as new hire',
        caption: nhDate ? captionForDate(nhDate) : undefined,
        primary: !oe,
        ariaLabel: nhDate
          ? `Enroll as new hire, coverage effective ${nhDate}`
          : 'Enroll as new hire',
      });
    }

    if (oe && nh && options.length === 2) {
      options[0].primary = true;
      options[1].primary = false;
    }

    return options;
  }

  /** Coverage date for the selected path, from the landing payload (no extra API call). */
  static pathEffectiveDateIso(
    info: EnrollmentLandingPageInfo | null | undefined,
    type: EnrollmentType | null | undefined
  ): string | null | undefined {
    if (!info || !type) return null;
    if (info.alwaysOpenEnrollment === true || type === 'NH') return info.newHireEffectiveDate;
    return info.effectiveDate;
  }

  static hasProductsOnPath(
    info: EnrollmentLandingPageInfo | null | undefined,
    type: EnrollmentType,
    products?: MasterClientProductViewDto[] | null
  ): boolean {
    const iso = EnrollmentLandingPathRules.pathEffectiveDateIso(info, type);
    if (!iso) return false;
    return filterProductsActiveOn(products ?? info?.products ?? [], iso).length > 0;
  }

  /** Flag-based paths that also have at least one product active on that path's effective date. */
  static availablePathOptions(
    info: EnrollmentLandingPageInfo | null | undefined,
    products?: MasterClientProductViewDto[] | null
  ): LandingPathOption[] {
    const catalog = products ?? info?.products ?? [];
    return EnrollmentLandingPathRules.buildPathOptions(info)
      .filter(path => EnrollmentLandingPathRules.hasProductsOnPath(info, path.type, catalog));
  }

  /** When only one path exists, returns OE or NH; otherwise null (caller uses query or redirect). */
  static inferSinglePathEnrollmentType(
    info: EnrollmentLandingPageInfo | null | undefined,
    products?: MasterClientProductViewDto[] | null
  ): EnrollmentType | null {
    const paths = EnrollmentLandingPathRules.availablePathOptions(info, products);
    return paths.length === 1 ? paths[0].type : null;
  }

  /**
   * Aligns `/enroll` query resolution with landing CTAs — single place for wizard init + draft fallback.
   */
  static resolveEnrollmentTypeForWizard(
    info: EnrollmentLandingPageInfo | null | undefined,
    enrollmentTypeQuery: string | null | undefined,
    products?: MasterClientProductViewDto[] | null
  ): LandingEnrollmentWizardResolution {
    const catalog = products ?? info?.products ?? [];
    if (info?.alwaysOpenEnrollment === true) {
      return { enrollmentType: 'OE', redirectToPathPicker: false };
    }

    const parsed = EnrollmentLandingPathRules.parseEnrollmentTypeQuery(enrollmentTypeQuery);
    if (parsed && EnrollmentLandingPathRules.hasProductsOnPath(info, parsed, catalog)) {
      return { enrollmentType: parsed, redirectToPathPicker: false };
    }
    const paths = EnrollmentLandingPathRules.availablePathOptions(info, catalog);
    if (paths.length === 1) {
      return { enrollmentType: paths[0].type, redirectToPathPicker: false };
    }
    if (paths.length > 1) {
      return { enrollmentType: null, redirectToPathPicker: true };
    }
    return { enrollmentType: null, redirectToPathPicker: false };
  }
}
